import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import type { DocumentVerificationStatus, VerificationQueueItem } from '../lib/types.ts';

const STATUS_META: Record<DocumentVerificationStatus, { label: string; cls: string }> = {
  auto_passed: { label: 'Auto-passed', cls: 'bg-emerald-50 text-emerald-700' },
  needs_review: { label: 'Needs review', cls: 'bg-amber-50 text-amber-700' },
  manual_passed: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-700' },
  manual_failed: { label: 'Rejected', cls: 'bg-red-50 text-red-700' },
};

const TABS: { key: DocumentVerificationStatus | 'all'; label: string }[] = [
  { key: 'needs_review', label: 'Needs review' },
  { key: 'auto_passed', label: 'Auto-passed' },
  { key: 'manual_passed', label: 'Approved' },
  { key: 'manual_failed', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

export function AdminVerifications() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<DocumentVerificationStatus | 'all'>('needs_review');

  const { data, isLoading } = useQuery({
    queryKey: ['verifications', tab],
    queryFn: () =>
      adminApi.get<{ verifications: VerificationQueueItem[] }>('/api/verifications', {
        query: tab === 'all' ? {} : { status: tab },
      }),
  });

  const review = useMutation({
    mutationFn: (input: { id: string; decision: 'pass' | 'fail' }) =>
      adminApi.patch(`/api/verifications/${input.id}`, { decision: input.decision }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['verifications'] }),
  });

  const items = data?.verifications ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Document verification</h1>
        <p className="mt-1 text-sm text-slate-500">
          AI checks uploaded documents for the fields configured on each document type.
          High-confidence uploads pass automatically; anything below the threshold lands here for
          a human to approve or reject.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`border-b-2 px-3 py-2 text-sm transition ${
              tab === t.key
                ? 'border-blue-600 font-semibold text-blue-700'
                : 'border-transparent font-medium text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <CenteredSpinner />
      ) : items.length === 0 ? (
        <EmptyState title="Nothing here" description="No verifications match this filter." />
      ) : (
        <div className="space-y-3">
          {items.map((v) => {
            const sub = v.submission_file?.submission;
            const owner = sub?.company?.company_name ?? sub?.driver?.name ?? 'Unknown';
            const meta = STATUS_META[v.status];
            return (
              <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">
                        {v.submission_file?.file_name ?? 'File'}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-xs ${meta.cls}`}>
                        {meta.label}
                      </span>
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {owner}
                      {sub?.question?.label ? ` · ${sub.question.label}` : ''} · overall confidence{' '}
                      {Math.round(v.overall_confidence * 100)}%
                    </div>
                  </div>
                  {v.status === 'needs_review' && (
                    <div className="flex gap-2">
                      <Btn
                        size="sm"
                        variant="secondary"
                        loading={review.isPending && review.variables?.id === v.id}
                        onClick={() => review.mutate({ id: v.id, decision: 'pass' })}
                      >
                        Approve
                      </Btn>
                      <Btn
                        size="sm"
                        variant="ghost"
                        onClick={() => review.mutate({ id: v.id, decision: 'fail' })}
                      >
                        Reject
                      </Btn>
                    </div>
                  )}
                </div>

                <div className="mt-3 grid gap-1 sm:grid-cols-2">
                  {v.result.map((r) => (
                    <div
                      key={r.field_key}
                      className="flex items-center justify-between rounded-md bg-slate-50 px-2.5 py-1.5 text-xs"
                    >
                      <span className="text-slate-700">{r.label}</span>
                      <span
                        className={
                          r.found && r.confidence >= 0.8
                            ? 'font-medium text-emerald-700'
                            : r.found
                              ? 'font-medium text-amber-600'
                              : 'font-medium text-red-600'
                        }
                      >
                        {r.found ? `${Math.round(r.confidence * 100)}%` : 'not found'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {review.error && <ErrorMessage error={review.error} />}
    </div>
  );
}
